import i18next from "../../i18n";
import { OffersToAccept, OrderResponse } from "../../types";
import { getRequestedVolumes } from "./awardJointFormat";

export const actions = {
  CLEAR_OFFERS: "CLEAR_OFFERS",
  UPDATE_VOLUME: "UPDATE_VOLUME",
  UPDATE_PRICE: "UPDATE_PRICE",
  DELETE_OFFER: "DELETE_OFFER",
  UPDATE_ON_CHECKBOX_CHANGE: "UPDATE_ON_CHECKBOX_CHANGE",
  SELECT_BEST_OFFERS: "SELECT_BEST_OFFERS",
  SELECT_ONE_SUPPLIER: "SELECT_ONE_SUPPLIER",
  /** Pre-select exactly the offers the offer assistant recommended (`action.offerIds`). */
  APPLY_RECOMMENDATION: "APPLY_RECOMMENDATION",
};

export type EventState = {
  offersToAccept: OffersToAccept[];
  errors: any[];
  invalid: any[];
};

export const initialEventState: EventState = {
  offersToAccept: [],
  errors: [],
  invalid: [],
};

/**
 * Joint award reducer, factored out of `AwardJoint.tsx` verbatim. Closes over the current
 * `order` list (requested-quantity ceilings) and `formattedOffers` (best-offer selection);
 * rebuilt on each render exactly as the inline version was.
 */
export const createAwardJointReducer =
  (order: OrderResponse[], formattedOffers: any[]) =>
  (state: EventState, action: any): EventState => {
    let newState = { ...state };
    switch (action.type) {
      case actions.UPDATE_ON_CHECKBOX_CHANGE:
        {
          const offerIndex = newState.offersToAccept.findIndex(
            (offer: OffersToAccept) => offer?.offer.id === action.id
          );
          if (offerIndex >= 0) {
            newState.offersToAccept[offerIndex] = {
              ...newState.offersToAccept[offerIndex],
              volume: action.volume,
              price: action.price,
            };
          } else {
            newState.offersToAccept.push({
              id: action.id,
              volume: action.volume,
              price: action.price,
              orderId: action.orderId,
              material: action.material,
              offer: action.offer,
            });
          }
        }
        break;
      case actions.UPDATE_VOLUME:
        {
          const offerIndex = newState.offersToAccept.findIndex(
            (offer) => offer?.offer.id === action.id
          );
          if (offerIndex >= 0) {
            newState.offersToAccept[offerIndex] = {
              ...newState.offersToAccept[offerIndex],
              volume: action.volume,
            };
          } else {
            newState.offersToAccept.push({
              id: action.id,
              volume: action.volume,
              orderId: action.orderId,
            } as OffersToAccept);
          }
        }
        break;

      case actions.UPDATE_PRICE:
        {
          const offerIndex = newState.offersToAccept.findIndex(
            (offer) => offer?.offer.id === action.id
          );
          if (offerIndex >= 0) {
            newState.offersToAccept[offerIndex] = {
              ...newState.offersToAccept[offerIndex],
              price: action.price,
            };
          } else {
            newState.offersToAccept.push({
              id: action.id,
              price: action.price,
              orderId: action.orderId,
            } as OffersToAccept);
          }
        }
        break;
      case actions.SELECT_BEST_OFFERS:
        {
          const bestOffers: any = [];
          newState.offersToAccept = [];
          formattedOffers.forEach((offerGroup) => {
            offerGroup.forEach((offer: any) => {
              if (offer.isBestOffer) {
                bestOffers.push({
                  id: offer.offer.id,
                  volume: offer.offer.order.quantity,
                  price: offer.offer.price,
                  orderId: offer.offer.order.id,
                  material: offer.material,
                  offer: offer.offer,
                });
              }
            });
          });
          newState.offersToAccept = bestOffers;
        }
        break;
      case actions.APPLY_RECOMMENDATION:
        {
          // Per material line: replace the selection with the recommended offers (full requested
          // volume, same entry shape as SELECT_BEST_OFFERS). Ids not in the matrix are ignored;
          // validation lists are rebuilt from scratch.
          const ids = new Set<string>(action.offerIds ?? []);
          const recommended: OffersToAccept[] = [];
          formattedOffers.forEach((offerGroup) => {
            offerGroup.forEach((cell: any) => {
              const offer = cell?.offer;
              if (!offer || !ids.has(offer.id)) return;
              if (recommended.some((r) => r.id === offer.id)) return;
              recommended.push({
                id: offer.id,
                volume: offer.order.quantity,
                price: offer.price,
                orderId: offer.order.id,
                material: cell.material,
                offer,
              });
            });
          });
          newState = { offersToAccept: recommended, errors: [], invalid: [] };
        }
        break;
      case actions.SELECT_ONE_SUPPLIER:
        newState.offersToAccept = [];
        newState.offersToAccept = action.payload;
        break;
      case actions.DELETE_OFFER:
        newState = {
          ...newState,
          offersToAccept: state.offersToAccept.filter(
            (offer) => offer?.offer.id !== action.id
          ),
        };
        break;
      case actions.CLEAR_OFFERS:
        newState = {
          ...newState,
          offersToAccept: [],
        };
        break;
    }

    const requestedVolumes = getRequestedVolumes(order);

    const volumeSums: any = {};
    newState.offersToAccept.forEach((offer) => {
      const isInvalidVolume =
        offer?.offer.volume <= 0 || offer?.offer.volume === null;
      const isInvalidPrice =
        offer?.offer.price <= 0 || offer?.offer.price === null;
      const errorIndex = newState.invalid.findIndex(
        (error) => error.id === offer?.offer.id
      );

      if (isInvalidVolume || isInvalidPrice) {
        const errorMessage = i18next.t("tenders.errors.invalid");

        if (errorIndex === -1) {
          newState.invalid.push({ id: offer.id, message: errorMessage });
        } else {
          newState.invalid[errorIndex] = {
            id: offer.id,
            message: errorMessage,
          };
        }
      } else if (errorIndex !== -1) {
        newState.invalid.splice(errorIndex, 1);
      }

      volumeSums[offer.orderId] =
        (volumeSums[offer.orderId] || 0) +
        parseFloat(String(offer.volume || 0));
    });

    Object.keys(volumeSums).forEach((orderId) => {
      const requestedVolume = requestedVolumes[orderId];
      const errorIndex = newState.errors.findIndex(
        (error) => error.orderId === orderId
      );

      const material = newState.offersToAccept.find(
        (o) => o.orderId === orderId
      )?.material;

      if (volumeSums[orderId] > requestedVolume) {
        const errorMessage = `${i18next.t("tenders.errors.message")} ${
          volumeSums[orderId]
        } > ${requestedVolume}`;

        if (errorIndex === -1) {
          newState.errors.push({ orderId, message: errorMessage, material });
        } else {
          newState.errors[errorIndex] = {
            orderId,
            material,
            message: errorMessage,
          };
        }
      } else if (errorIndex !== -1) {
        newState.errors.splice(errorIndex, 1);
      }
    });

    return newState;
  };
